import { ChartBarIcon, UserGroupIcon } from "@heroicons/react/24/outline";

import Headline from "~/components/Headline/Headline";
import { FigureRow } from "~/modules/dashboard/components/FigureRow.tsx";
import { useKpiFigures } from "~/modules/dashboard/hooks/useKpiFigures.ts";
import { formatPercentage } from "~/utils/numberUtils.ts";

// The personal utilization has a target, so it shows a traffic light: green at or above the target,
// orange up to 10 points below it, red further below. The company figures have no target, so they
// stay neutral. A missing value is gray.
const ORANGE_BAND_POINTS = 10;

// The personal utilization row always shows, so a user without a value sees why. The API does not name
// the reason for a missing `personal` (freelancer, working student, or a career level without a target),
// so the tooltip is general.
const NO_PERSONAL_UTILISATION_TOOLTIP = "Für dich wird keine Auslastung berechnet, zum Beispiel als Freelancer, "
	+ "Werkstudent oder ohne Zielwert der Karrierestufe.";
const NO_YEAR_VALUE_TOOLTIP = "Noch kein Wert in diesem Jahr";

function utilisationClassName(percentage: number | null, target: number | null): string {
	if (percentage === null) {
		return "text-gray-500";
	}
	if (target === null) {
		return "text-gray-900";
	}
	if (percentage >= target) {
		return "text-success-500";
	}

	return percentage >= target - ORANGE_BAND_POINTS ? "text-orange-500" : "text-danger-500";
}

// "Ø Tagessatz NN" is hidden in the UI only: the client does not want the row, the API keeps the field.
export function KpiFiguresBox() {
	const { data, isPending, isError } = useKpiFigures();
	const personal = data?.personal;
	const personalPercentage = personal?.yearToDate.utilisationPercentage ?? null;
	const personalTooltip = personal ? NO_YEAR_VALUE_TOOLTIP : NO_PERSONAL_UTILISATION_TOOLTIP;

	return (
		<div>
			<Headline type="h3" size="lg" weight="semibold" className="mb-3 flex min-h-7 items-center">YTD KPIs</Headline>
			<div className="rounded-lg border border-gray-200 bg-white p-3">
				{isError && <p className="text-sm text-gray-500">Die Kennzahlen konnten nicht geladen werden.</p>}
				{!isError && (
					<dl>
						<FigureRow icon={ChartBarIcon}
								   label="Ø Auslastung"
								   value={formatPercentage(personalPercentage)}
								   isLoading={isPending}
								   tooltip={personalPercentage === null ? personalTooltip : undefined}
								   valueClassName={utilisationClassName(
									   personalPercentage,
									   personal?.utilisationTargetPercentage ?? null
								   )} />
						<FigureRow icon={UserGroupIcon}
								   label='Ø Auslastung NN'
								   value={formatPercentage(data?.company.utilisation?.utilisationPercentage ?? null)}
								   isLoading={isPending} />
					</dl>
				)}
			</div>
		</div>
	);
}
